import { RiPencilFill, RiSendToBack, RiSendBackward, RiBringForward, RiBringToFront } from "react-icons/ri"
import { LuMousePointer2, LuUndo2, LuRedo2, LuDownload, LuUpload, LuImage, LuScissors, LuSparkles } from "react-icons/lu"
import { TbLayersSelectedBottom } from "react-icons/tb"
import { FiZap, FiLayers } from "react-icons/fi"
import Button from "@/components/ui/Button"
import ColorPicker from "@/components/ui/ColorPicker"
import { Slider } from "@/components/ui/shadcn/slider"

export default function Toolbar({
    brushColor,
    brushSize,
    mode,
    bgColor,
    onBgColorChange,
    onColorChange,
    onSizeChange,
    onModeChange,
    onClear,
    onExport,
    onUploadImage,
    onAddFromAssets,
    onUndo,
    onRedo,
    canUndo,
    canRedo,
    onReorderElements,
    hasSelection,
    onionImageCount = 0,
    onionEnabled = false,
    onToggleOnion,
    onionCount = 0,
    onOnionCountChange,
    onionOpacity = 0.6,
    onOnionOpacityChange,
    generationsOpen = false,
    onToggleGenerations,
    hasImageSelection = false,
    onRemoveBg,
    removingBg = false,
    onCompose,
    composing = false,
}) {
    const sizes = [2, 4, 8, 16, 32]

    const activeBtn = "bg-primary text-primary-foreground"
    const inactiveBtn = "bg-secondary text-secondary-foreground hover:bg-secondary/80 cursor-pointer"
    const disabledBtn = "disabled:opacity-40 disabled:cursor-default"

    return (
        <div className="flex items-center p-2 border-b border-secondary/40 flex-shrink-0 justify-between bg-primary-foreground/40">
            <div className="flex items-center gap-4">
                {/* Mode selector */}
                <div className="flex items-center rounded-md overflow-hidden">
                    <button
                        onClick={() => onModeChange('transform')}
                        className={`flex items-center py-1 px-2 ${mode === 'transform' ? activeBtn : inactiveBtn}`}
                        title="Select / Transform (V)"
                    >
                        <LuMousePointer2 className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => onModeChange('draw')}
                        className={`flex items-center py-1 px-2 ${mode === 'draw' ? activeBtn : inactiveBtn}`}
                        title="Draw (D)"
                    >
                        <RiPencilFill className="w-4 h-4" />
                    </button>
                </div>

                {/* BG color + Brush color */}
                <div className="flex items-center gap-3">
                    {bgColor !== undefined && (
                        <ColorPicker
                            label="BG"
                            value={bgColor}
                            onChange={onBgColorChange}
                            swatchClassName="w-8 h-8"
                        />
                    )}
                    <ColorPicker
                        label="Brush"
                        value={brushColor}
                        onChange={onColorChange}
                        allowAlpha={false}
                        swatchClassName="w-8 h-8"
                    />
                </div>

                {/* Brush sizes */}
                <div className="flex items-center rounded-md overflow-hidden">
                    {sizes.map(s => (
                        <button
                            key={s}
                            onClick={() => onSizeChange(s)}
                            className={`flex items-center justify-center w-6 h-6 ${brushSize === s ? activeBtn : inactiveBtn}`}
                            title={`Size ${s}`}
                        >
                            <div
                                className="rounded-full bg-current"
                                style={{ width: Math.min(s, 14), height: Math.min(s, 14) }}
                            />
                        </button>
                    ))}
                </div>

                {/* Upload / Assets */}
                <div className="flex items-center rounded-md overflow-hidden">
                    <button
                        onClick={onUploadImage}
                        className={`flex items-center py-1 px-2 ${inactiveBtn}`}
                        title="Upload image"
                    >
                        <LuUpload className="w-4 h-4" />
                    </button>
                    <button
                        onClick={onAddFromAssets}
                        className={`flex items-center py-1 px-2 ${inactiveBtn}`}
                        title="Add from assets"
                    >
                        <LuImage className="w-4 h-4" />
                    </button>
                </div>

                {/* Undo / Redo */}
                <div className="flex items-center rounded-md overflow-hidden">
                    <button
                        onClick={onUndo}
                        disabled={!canUndo}
                        className={`flex items-center py-1 px-2 ${inactiveBtn} ${disabledBtn}`}
                        title="Undo (Cmd+Z)"
                    >
                        <LuUndo2 className="w-4 h-4" />
                    </button>
                    <button
                        onClick={onRedo}
                        disabled={!canRedo}
                        className={`flex items-center py-1 px-2 ${inactiveBtn} ${disabledBtn}`}
                        title="Redo (Cmd+Shift+Z)"
                    >
                        <LuRedo2 className="w-4 h-4" />
                    </button>
                </div>

                {/* Z-order */}
                <div className="flex items-center rounded-md overflow-hidden">
                    <button
                        disabled={!hasSelection}
                        onClick={() => onReorderElements('sendToBack')}
                        className={`flex items-center py-1 px-2 ${inactiveBtn} ${disabledBtn}`}
                        title="Send to back"
                    >
                        <RiSendToBack className="w-4 h-4" />
                    </button>
                    <button
                        disabled={!hasSelection}
                        onClick={() => onReorderElements('moveBackward')}
                        className={`flex items-center py-1 px-2 ${inactiveBtn} ${disabledBtn}`}
                        title="Move backward"
                    >
                        <RiSendBackward className="w-4 h-4" />
                    </button>
                    <button
                        disabled={!hasSelection}
                        onClick={() => onReorderElements('moveForward')}
                        className={`flex items-center py-1 px-2 ${inactiveBtn} ${disabledBtn}`}
                        title="Move forward"
                    >
                        <RiBringForward className="w-4 h-4" />
                    </button>
                    <button
                        disabled={!hasSelection}
                        onClick={() => onReorderElements('bringToFront')}
                        className={`flex items-center py-1 px-2 ${inactiveBtn} ${disabledBtn}`}
                        title="Bring to front"
                    >
                        <RiBringToFront className="w-4 h-4" />
                    </button>
                </div>
                <Button
                    type="secondary"
                    icon={<LuScissors className="w-4 h-4" />}
                    disabled={!hasImageSelection || removingBg}
                    onClick={onRemoveBg}
                    size="sm"
                    title="Remove background"
                >
                    <span className="hidden lg:block">{removingBg ? 'Removing...' : 'Remove BG'}</span>
                </Button>
                <Button
                    type="secondary"
                    icon={<LuSparkles className="w-4 h-4" />}
                    disabled={composing}
                    onClick={onCompose}
                    size="sm"
                    title="Compose layers with AI"
                >
                    <span className="hidden lg:block">{composing ? 'Composing...' : 'Compose'}</span>
                </Button>

                {onionImageCount > 0 && (
                    <div className="flex items-center gap-2 bg-secondary/50 rounded-md">
                        <button
                            onClick={onToggleOnion}
                            className={`flex items-center p-2 rounded-md ${onionEnabled ? activeBtn : inactiveBtn}`}
                            title="Toggle onion skin (O)"
                        >
                            <TbLayersSelectedBottom className="w-4 h-4" />
                        </button>
                        {onionEnabled && (
                            <div className="flex flex-col justify-center">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-secondary-foreground/50 w-10 text-right tabular-nums">Amount</span>
                                    <Slider
                                        value={[onionCount]}
                                        min={0}
                                        max={onionImageCount}
                                        step={1}
                                        onValueChange={([v]) => onOnionCountChange?.(v)}
                                        className="w-24"
                                    />
                                    <span className="text-[10px] text-secondary-foreground/50 tabular-nums w-6">
                                        {onionCount}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-secondary-foreground/50 w-10 text-right tabular-nums">Opacity</span>
                                    <Slider
                                        value={[Math.round(onionOpacity * 100)]}
                                        min={0}
                                        max={100}
                                        step={1}
                                        onValueChange={([v]) => onOnionOpacityChange?.(v / 100)}
                                        className="w-24"
                                    />
                                    <span className="text-[10px] text-secondary-foreground/50 w-6  tabular-nums">
                                        {Math.round(onionOpacity * 100)}%
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <Button type="secondary" icon={<LuDownload className="w-4 h-4" />} onClick={onExport} size="sm" title="Export final image">
                    <span className="hidden lg:block">Download</span>
                </Button>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2">
                <div className="flex items-center rounded-md overflow-hidden">
                    <button
                        onClick={() => { if (generationsOpen) onToggleGenerations?.() }}
                        className={`flex items-center p-2 ${!generationsOpen ? activeBtn : inactiveBtn}`}
                        title="Layers"
                    >
                        <FiLayers className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => { if (!generationsOpen) onToggleGenerations?.() }}
                        className={`flex items-center p-2 ${generationsOpen ? activeBtn : inactiveBtn}`}
                        title="GenAI"
                    >
                        <FiZap className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    )
}
